/**
 * Critically damped smoothing for the camera (docs/DRIVING.md §6).
 *
 * `damp()` (core/math) is a first-order filter: the value heads for the target at a speed proportional to
 * the gap, so a target that jumps (the look-ahead flipping into reverse, the zoom stepping when the pedal
 * comes off) gives the frame a velocity step on the spot. A critically damped spring tracks a moving target
 * as closely, but its velocity is continuous: a jump in the target starts a smooth curve, never a kink,
 * and it never overshoots. After a step it is 60 % of the way at `smoothTime` and 90 % at twice that.
 * The integration is the classic game-gems form (stable for any dt).
 */
export class SmoothValue {
  value: number;
  velocity = 0;

  constructor(value = 0) {
    this.value = value;
  }

  /** Jump to `v` with no velocity (teleports, snaps). */
  set(v: number): void {
    this.value = v;
    this.velocity = 0;
  }

  /** Advance towards `target` by `dt` seconds; returns the new value. */
  to(target: number, smoothTime: number, dt: number): number {
    if (dt <= 0) return this.value;
    const st = Math.max(1e-4, smoothTime);
    const omega = 2 / st;
    const x = omega * dt;
    const exp = 1 / (1 + x + 0.48 * x * x + 0.235 * x * x * x);
    const change = this.value - target;
    const temp = (this.velocity + omega * change) * dt;
    this.velocity = (this.velocity - omega * temp) * exp;
    let out = target + (change + temp) * exp;
    // Never past the target.
    if (target - this.value > 0 === out > target) {
      out = target;
      this.velocity = 0;
    }
    this.value = out;
    return out;
  }
}

/** Two `SmoothValue`s for a ground point. */
export class SmoothPoint {
  readonly x: SmoothValue;
  readonly z: SmoothValue;

  constructor(x = 0, z = 0) {
    this.x = new SmoothValue(x);
    this.z = new SmoothValue(z);
  }

  set(x: number, z: number): void {
    this.x.set(x);
    this.z.set(z);
  }

  to(tx: number, tz: number, smoothTime: number, dt: number): void {
    this.x.to(tx, smoothTime, dt);
    this.z.to(tz, smoothTime, dt);
  }
}

/**
 * A speed read for the zoom: it follows a rising speed within about a second and a falling one over a few,
 * so a tap on the brake or a lift off the gas does not pump the frame in and out. Returns the new value.
 */
export function zoomSpeedStep(current: number, speed: number, dt: number, riseRate = 2, fallRate = 0.6): number {
  const rate = speed > current ? riseRate : fallRate;
  return current + (speed - current) * (1 - Math.exp(-rate * dt));
}
